const TITLE_GLITCH_CONFIG = {
    amplitude: 14,
    bands: 12,
    black: 0.1,
    decay: 2.2,
    delay: 500,
    drop: 0.15,
    duration: 850,
    jitter: 5,
    supersampling: 2,
};

export const TITLE_IDLE_GLITCH_CONFIG = {
    amplitude: 10,
    bands: 12,
    black: 0.07,
    decay: 2.2,
    drop: 0.1,
    jitter: 3.5,
    maxDelay: 3500,
    maxDuration: 1000,
    minDelay: 3000,
    minDuration: 500,
    threshold: 0.25,
};

class TitleGlitchItem {
    constructor($title, observe = true) {
        this.$title = $title;

        this.$canvas = this.$title.querySelector(".c-title-glitch__canvas");
        this.isReady = false;

        if (!this.$canvas || !this.$canvas.getContext("2d")) return;

        this.isReady = true;
        this.$title.classList.add("-ready");

        if (observe) this._observe();
    }

    _observe() {
        if (!("IntersectionObserver" in window)) {
            this._start();
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;

                observer.disconnect();
                this._start();
            },
            { threshold: 0.4 }
        );

        observer.observe(this.$title);
    }

    _start() {
        window.setTimeout(async () => {
            const frame = await this.prepare();

            if (!frame) return;

            window.requestAnimationFrame((startTime) => {
                this._run(frame.base, frame.scale, startTime, () => this._startIdle(frame));
            });
        }, TITLE_GLITCH_CONFIG.delay);
    }

    prepare() {
        return new Promise((resolve) => {
            const image = new Image();

            image.onload = () => resolve(this._rasterize([image]));
            image.onerror = () => {
                this._showFallback();
                resolve(null);
            };
            image.src = this.$title.dataset.svg;
        });
    }

    _rasterize(images) {
        const scale = TITLE_GLITCH_CONFIG.supersampling;
        const width = images[0].naturalWidth * scale;
        const height = images[0].naturalHeight * scale;

        if (!width || !height) {
            this._showFallback();
            return null;
        }

        const frames = images.map((image) => {
            const frame = document.createElement("canvas");
            const frameContext = frame.getContext("2d");

            frame.width = width;
            frame.height = height;
            frameContext.drawImage(image, 0, 0, width, height);

            return frame;
        });

        const base = frames[0];
        const baseContext = base.getContext("2d");

        if (!baseContext) {
            this._showFallback();
            return null;
        }

        // SVGフィルタは一度だけCanvasへ焼き込み、iOSで毎フレーム再計算させない。
        if (!this._hasVisiblePixels(baseContext, width, height)) {
            this._showFallback();
            return null;
        }

        this.$canvas.width = width;
        this.$canvas.height = height;

        return { base, scale };
    }

    _hasVisiblePixels(context, width, height) {
        try {
            const pixels = context.getImageData(0, 0, width, height).data;

            for (let index = 3; index < pixels.length; index += 4) {
                if (pixels[index] > 10) return true;
            }

            return false;
        } catch (error) {
            return true;
        }
    }

    _run(base, scale, startTime = 0, onComplete = null, config = TITLE_GLITCH_CONFIG, canContinue = null, onRaf = null) {
        const context = this.$canvas.getContext("2d");
        const bandHeight = base.height / config.bands;

        const drawSlices = (amplitude, drop, offset) => {
            for (let band = 0; band < config.bands; band += 1) {
                if (Math.random() < drop) continue;

                const y = band * bandHeight;
                const x = (offset + (Math.random() * 2 - 1) * amplitude) * scale;

                context.drawImage(base, 0, y, base.width, bandHeight, x, y, base.width, bandHeight);
            }
        };

        const requestNextFrame = () => {
            const raf = window.requestAnimationFrame(tick);

            if (onRaf) onRaf(raf);
        };

        const tick = (now) => {
            if (canContinue && !canContinue()) {
                context.clearRect(0, 0, this.$canvas.width, this.$canvas.height);
                context.drawImage(base, 0, 0);
                if (onComplete) onComplete(false);
                return;
            }

            if (!startTime) startTime = now;

            const progress = (now - startTime) / config.duration;

            context.clearRect(0, 0, this.$canvas.width, this.$canvas.height);

            if (progress >= 1) {
                context.drawImage(base, 0, 0);
                if (onComplete) onComplete(true);
                return;
            }

            const attenuation = (1 - progress) ** config.decay;

            if (progress < 0.9 && Math.random() < config.black * attenuation + 0.015) {
                requestNextFrame();
                return;
            }

            drawSlices(attenuation * config.amplitude, attenuation * config.drop, (Math.random() * 2 - 1) * attenuation * config.jitter);
            requestNextFrame();
        };

        requestNextFrame();
    }

    _startIdle(frame) {
        const svgPath = this.$title.dataset.svg || "";

        if (!/\/ttl\.svg(?:\?|$)/.test(svgPath) || this.idleStarted) return;

        this.idleStarted = true;
        this.idleFrame = frame;
        this.idleInView = false;
        this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        this._handleIdleAvailability = this._handleIdleAvailability.bind(this);
        document.addEventListener("visibilitychange", this._handleIdleAvailability);

        if (this.reducedMotionQuery.addEventListener) {
            this.reducedMotionQuery.addEventListener("change", this._handleIdleAvailability);
        } else {
            this.reducedMotionQuery.addListener(this._handleIdleAvailability);
        }

        if (!("IntersectionObserver" in window)) {
            this.idleInView = true;
            this._scheduleIdle();
            return;
        }

        this.idleObserver = new IntersectionObserver(
            (entries) => {
                this.idleInView = entries[0].isIntersecting;
                this._handleIdleAvailability();
            },
            { threshold: TITLE_IDLE_GLITCH_CONFIG.threshold }
        );
        this.idleObserver.observe(this.$title);
    }

    _handleIdleAvailability() {
        if (this._canRunIdle()) {
            this._scheduleIdle();
            return;
        }

        this._stopIdle();
    }

    _canRunIdle() {
        return this.idleInView && !document.hidden && !this.reducedMotionQuery.matches;
    }

    _scheduleIdle() {
        window.clearTimeout(this.idleTimer);

        if (!this._canRunIdle() || this.idleRaf) return;

        const delay = TITLE_IDLE_GLITCH_CONFIG.minDelay + Math.random() * (TITLE_IDLE_GLITCH_CONFIG.maxDelay - TITLE_IDLE_GLITCH_CONFIG.minDelay);

        this.idleTimer = window.setTimeout(() => this._runIdleBurst(), delay);
    }

    _runIdleBurst() {
        this.idleTimer = null;

        if (!this._canRunIdle()) return;

        const duration = TITLE_IDLE_GLITCH_CONFIG.minDuration + Math.random() * (TITLE_IDLE_GLITCH_CONFIG.maxDuration - TITLE_IDLE_GLITCH_CONFIG.minDuration);
        const config = { ...TITLE_IDLE_GLITCH_CONFIG, duration };

        this._run(
            this.idleFrame.base,
            this.idleFrame.scale,
            performance.now(),
            (completed) => {
                this.idleRaf = null;
                if (completed) this._scheduleIdle();
            },
            config,
            () => this._canRunIdle(),
            (raf) => {
                this.idleRaf = raf;
            }
        );
    }

    _settleIdle() {
        if (!this.idleFrame) return;

        const context = this.$canvas.getContext("2d");

        context.clearRect(0, 0, this.$canvas.width, this.$canvas.height);
        context.drawImage(this.idleFrame.base, 0, 0);
    }

    _stopIdle() {
        window.clearTimeout(this.idleTimer);
        this.idleTimer = null;

        if (this.idleRaf) window.cancelAnimationFrame(this.idleRaf);

        this.idleRaf = null;
        this._settleIdle();
    }

    _showFallback() {
        this.$title.classList.add("-fallback");
    }
}

class TitleGlitchGroup {
    constructor($group) {
        this.$group = $group;
        this.items = Array.from(this.$group.querySelectorAll(".js-howto4th-title-glitch"))
            .map(($title) => new TitleGlitchItem($title, false))
            .filter((item) => item.isReady);

        if (!this.items.length) return;

        this._observe();
    }

    _observe() {
        if (!("IntersectionObserver" in window)) {
            this._start();
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;

                observer.disconnect();
                this._start();
            },
            { threshold: 0.4 }
        );

        observer.observe(this.$group);
    }

    _start() {
        window.setTimeout(async () => {
            const frames = await Promise.all(this.items.map((item) => item.prepare()));

            window.requestAnimationFrame((startTime) => {
                frames.forEach((frame, index) => {
                    if (!frame) return;

                    this.items[index]._run(frame.base, frame.scale, startTime, () => this.items[index]._startIdle(frame));
                });
            });
        }, TITLE_GLITCH_CONFIG.delay);
    }
}

export default class TitleGlitch {
    constructor() {
        document.querySelectorAll(".js-howto4th-title-glitch-group").forEach(($group) => new TitleGlitchGroup($group));
        document.querySelectorAll(".js-howto4th-title-glitch").forEach(($title) => {
            if ($title.closest(".js-howto4th-title-glitch-group")) return;

            new TitleGlitchItem($title);
        });
    }
}
